import _ from 'lodash';
import dagre from 'dagre';
import { isNode } from 'react-flow-renderer';
import React from 'react';
import { WORKFLOW_NODE_LABELS, WORKFLOW_RESOURCE_STEP_TYPES, WORKFLOW_STEP_TYPES } from '../../../../global/constants';

const START = {
  id: 'START',
  data: {
    isEditable: false,
    isRemovable: false,
    label: 'Start',
  },
  className: 'node-size--mini border border-secondary',
};
const END = {
  id: 'END',
  data: {
    isEditable: false,
    isRemovable: false,
    label: 'End',
  },
  className: 'node-size--mini border border-secondary',
};

export class WorkflowGraph {
  constructor(workflowData) {
    this.hasAbTest = false;
    this.abNodeId = undefined;
    this.callbacks = {};
    this.nodes = new Map();
    this.width = 0;

    const numberOfNexts = new Map();

    if (!workflowData || !workflowData.steps) {
      return;
    }

    workflowData.steps.forEach(currentNode => {
      const step = _.cloneDeep(currentNode);
      this.nodes.set(step.id, step);
      step.isTreatment = false;

      // needed for later to figure out testEndNode
      this._incrementNextCount(numberOfNexts, step.next);

      if (step.type === WORKFLOW_STEP_TYPES.AB) {
        let lastUsableTreatmentNumber = 0;
        this.hasAbTest = true;
        const abAllocationParameters = step.parameters.allocations;
        const treatmentNexts = new Map();
        for (const allocation of abAllocationParameters) {
          treatmentNexts.set(allocation.name, allocation.next);
          const allocationNumber = parseInt(allocation.name.split(' ')[1]);
          if (allocationNumber > lastUsableTreatmentNumber) {
            lastUsableTreatmentNumber = allocationNumber;
          }
          if (allocation.next !== step.next) {
            this._incrementNextCount(numberOfNexts, allocation.next);
          }
        }
        step.treatments = treatmentNexts;
        step.lastUsableTreatmentNumber = lastUsableTreatmentNumber + 1;
        this.abNodeId = step.id;
      }

      if (step.next === undefined || step.next < 0) {
        this.endAt = step.id;
        step.next = -1;
      }
    });

    this.startAt = workflowData.startAt;

    // calculate previous, isTreatment, testEndNode
    // this should take care of control group
    let previous = undefined;
    let next = this.startAt;
    while (next >= 0) {
      const node = this.nodes.get(next);
      if (previous !== undefined) {
        node.previous = previous;
      }
      previous = node.id;
      next = node.next;
    }

    // This must happen AFTER calculating main node next/previouses, but
    // before we do next/previous calculations for treatment groups
    this._cleanupAbTestData();

    // treatment groups of ab
    if (this.hasAbTest) {
      // clean up orphaned A/B test nodes, due to an old bug
      const abNode = this.nodes.get(this.abNodeId);

      if (!abNode.testEndNode && abNode.treatments.size > 1) {
        for (const [, allocationStartId] of abNode.treatments) {
          if (allocationStartId === abNode.next) {
            continue;
          }
          let previous = undefined;
          let next = allocationStartId;
          while (numberOfNexts.get(next) === 1) {
            const node = this.nodes.get(next);
            node.isTreatment = true;
            if (previous === undefined) {
              node.previous = this.abNodeId;
            } else {
              node.previous = previous;
            }
            previous = node.id;
            next = node.next;
          }
          abNode.testEndNode = next;
        }
      }
    }

    // Calculate next available ID. We can't reuse IDs within a session or the browser will crash.
    const ids = [];
    for (let id of this.nodes.keys()) {
      ids.push(id);
    }
    this.nextFreeId = Math.max(...ids) + 1;
  }

  _cleanupAbTestData() {
    if (!this.hasAbTest) {
      return;
    }

    const abNode = this.nodes.get(this.abNodeId);

    // Check if the A/B test node was orphaned by an old bug that wouldn't
    // actually delete the A/B test from the steps graph.
    // Skip this check when the AB node is the first node (no previous).
    if (abNode.previous !== undefined) {
      const previousNode = this.nodes.get(abNode.previous);
      if (!previousNode || previousNode.next !== this.abNodeId) {
        this.hasAbTest = false;
        this.nodes.delete(this.abNodeId);
        this.abNodeId = '';
      }
    }

    // Clean up any treatment branches that don't exist
    for (const [allocationName, allocationStartId] of abNode.treatments) {
      const nextNode = this.nodes.get(allocationStartId);
      if (!nextNode) {
        // remove it from our local treatments tracking
        abNode.treatments.delete(allocationName);

        // and remove it from the allocations parameter, which is actually
        // used in RTS (and in TSI in the allocations popup)
        for (const allocation of abNode.parameters.allocations) {
          if (allocation.name === allocationName) {
            const idx = abNode.parameters.allocations.indexOf(allocation);
            abNode.parameters.allocations.splice(idx, 1);
            break;
          }
        }
      }
    }
  }

  _incrementNextCount(numberOfNexts, next) {
    if (!numberOfNexts.has(next)) {
      numberOfNexts.set(next, 1);
    } else {
      numberOfNexts.set(next, numberOfNexts.get(next) + 1);
    }
  }

  addAbTest(nodeBeforeId, lastTestNode, metricsRoleArn) {
    if (this.hasAbTest === true) {
      return;
    }
    const newNodeId = this._findFreeNodeId();
    const nodeBefore = this.nodes.get(nodeBeforeId);
    const nodeLast = this.nodes.get(lastTestNode);
    this.nodes.set(newNodeId, {
      id: newNodeId,
      next: nodeBefore.next,
      previous: nodeBeforeId,
      isTreatment: false,
      testEndNode: nodeLast.next,
      lastUsableTreatmentNumber: 1,
      type: WORKFLOW_STEP_TYPES.AB,
      treatments: new Map(),
      parameters: {
        buckets: 10000,
        allocations: [
          {
            start: 0,
            end: 9999,
            name: 'Control',
            next: nodeBefore.next,
          },
        ],
        chooseFrom: 'session',
        metricsRoleArn: metricsRoleArn,
      },
    });
    const abNode = this.nodes.get(newNodeId);
    abNode.treatments.set('Control', nodeBefore.next);
    const nodeAfter = this.nodes.get(nodeBefore.next);
    nodeBefore.next = newNodeId;
    nodeAfter.previous = newNodeId;

    // Mark control nodes as treatment
    let currentNode = this.nodes.get(abNode.next);
    while (currentNode && currentNode.id !== abNode.testEndNode) {
      currentNode.isTreatment = true;
      currentNode = this.nodes.get(currentNode.next);
    }

    this.hasAbTest = true;
    this.abNodeId = newNodeId;

    this.addTreatmentBranchForAbTest();
  }

  addNodeAfter(edgeId, typeOfNewNode) {
    const [edgeStartId, edgeEndId] = this._parseEdgeId(edgeId);
    const newNodeId = this._findFreeNodeId();
    const isTreatment = this._isAddedNodeTreatment(edgeStartId);
    this.nodes.set(newNodeId, this._createNewNode(newNodeId, typeOfNewNode, edgeEndId, edgeStartId, isTreatment, {}));
    const nodeBefore = this.nodes.get(edgeStartId);
    const nodeAfter = this.nodes.get(edgeEndId);
    if (nodeBefore.type === WORKFLOW_STEP_TYPES.AB) {
      if (nodeBefore.next === edgeEndId) {
        nodeBefore.next = newNodeId;
      }
      for (const treatmentGroupName of nodeBefore.treatments.keys()) {
        if (nodeBefore.treatments.get(treatmentGroupName) === edgeEndId) {
          nodeBefore.treatments.set(treatmentGroupName, newNodeId);
          break;
        }
      }
      for (const allocation of nodeBefore.parameters.allocations) {
        if (allocation.next === edgeEndId) {
          allocation.next = newNodeId;
        }
      }
    } else {
      nodeBefore.next = newNodeId;
    }
    nodeAfter.previous = newNodeId;
    this.tryMarkBranchUntested(newNodeId);
  }

  addNodeAfterAb(typeOfNewNode) {
    const nodesBeforeMerge = this._getNodesBeforeMerge();
    const abNode = this.getNode(this.abNodeId);
    const newNodeId = this._findFreeNodeId();
    const oldEndNode = this.getNode(abNode.testEndNode);

    this.nodes.set(
      newNodeId,
      this._createNewNode(newNodeId, typeOfNewNode, abNode.testEndNode, oldEndNode.previous, false, {})
    );

    abNode.testEndNode = newNodeId;
    for (const nodeId of nodesBeforeMerge) {
      const node = this.getNode(nodeId);
      node.next = newNodeId;
    }
    oldEndNode.previous = newNodeId;
  }

  _createNewNode(nodeId, type, next, previous, isTreatment, parameters) {
    return {
      id: nodeId,
      type: type,
      next: next,
      previous: previous,
      isTreatment: isTreatment,
      parameters: {},
    };
  }

  // All 'real' nodes (that correspond to actual steps) have an integer id.
  _findFreeNodeId() {
    return this.nextFreeId++;
  }

  _isAddedNodeTreatment(edgeStartId) {
    const nodeBefore = this.nodes.get(edgeStartId);
    if (nodeBefore.type === WORKFLOW_STEP_TYPES.AB) {
      return true;
    }
    return nodeBefore.isTreatment;
  }

  removeNode(nodeId) {
    const toRemove = this.nodes.get(nodeId);
    const previousNode = this.nodes.get(toRemove.previous);
    const nextNode = this.nodes.get(toRemove.next);

    this.tryMarkBranchUntested(nodeId);

    // if node to remove is at a start of a treatment branch
    if (previousNode.type === WORKFLOW_STEP_TYPES.AB && toRemove.isTreatment === true) {
      for (const [treatmentName, treatmentNext] of previousNode.treatments) {
        if (treatmentNext === nodeId) {
          previousNode.treatments.set(treatmentName, toRemove.next);
          break;
        }
      }
    }

    previousNode.next = toRemove.next;
    nextNode.previous = toRemove.previous;

    if (toRemove.type === WORKFLOW_STEP_TYPES.AB) {
      this._resolveAbTest(toRemove.next);
    }

    // if node to remove is where a/b test branches merge
    if (this.hasAbTest) {
      const abNode = this.nodes.get(this.abNodeId);
      if (abNode.testEndNode === nodeId) {
        abNode.testEndNode = toRemove.next;
      }
    }

    this.nodes.delete(nodeId);
  }

  resolveAbTest(controlNext) {
    const abNodeId = this.abNodeId;
    this._resolveAbTest(controlNext);
    this.nodes.delete(abNodeId);
  }

  _resolveAbTest(controlNext) {
    const abNode = this.nodes.get(this.abNodeId);
    const previousNode = this.nodes.get(abNode.previous);
    previousNode.next = controlNext;
    for (const [, treatmentNext] of abNode.treatments) {
      if (treatmentNext !== controlNext) {
        // remove treatments but not control branch
        this._removeTreatment(treatmentNext);
      } else {
        this._unflagTreatment(treatmentNext);
      }
    }
    this.hasAbTest = false;
    this.abNodeId = undefined;
  }

  _removeTreatment(treatmentStart) {
    let node = this.nodes.get(treatmentStart);
    const abNode = this.nodes.get(this.abNodeId);
    while (node.id !== abNode.testEndNode) {
      this.nodes.delete(node.id);
      node = this.nodes.get(node.next);
    }
  }

  _unflagTreatment(treatmentStart) {
    let node = this.nodes.get(treatmentStart);
    const abNode = this.nodes.get(this.abNodeId);
    while (node.id !== abNode.testEndNode) {
      node.isTreatment = false;
      node = this.nodes.get(node.next);
    }
  }

  addTreatmentBranchForAbTest() {
    const abNodeData = this.nodes.get(this.abNodeId);

    const nodesToCopy = [];
    let next = abNodeData.next;
    while (next !== abNodeData.testEndNode) {
      const nextNode = this.nodes.get(next);
      nodesToCopy.push(nextNode);
      next = nextNode.next;
    }

    const newNodes = [];
    for (const nodeToCopy of nodesToCopy) {
      const newId = this._findFreeNodeId();
      const newNode = _.cloneDeep(nodeToCopy);
      newNode.id = newId;
      this.nodes.set(newId, newNode);
      newNodes.push(newNode);
    }

    let previousNode = undefined;
    for (const newNode of newNodes) {
      if (previousNode === undefined) {
        newNode.previous = this.abNodeId;
      } else {
        newNode.previous = previousNode.id;
        previousNode.next = newNode.id;
      }
      previousNode = newNode;
    }
    newNodes[newNodes.length - 1].next = abNodeData.testEndNode;

    const lastUsableNumber = abNodeData.lastUsableTreatmentNumber;
    abNodeData.lastUsableTreatmentNumber = lastUsableNumber + 1;
    const newTreatmentGroupName = `Treatment ${lastUsableNumber}`;
    abNodeData.treatments.set(newTreatmentGroupName, newNodes[0].id);
    abNodeData.parameters.allocations.push({
      start: 0,
      end: 0,
      name: newTreatmentGroupName,
      next: newNodes[0].id,
      isTested: true,
    });
  }

  canEditNode(nodeId) {
    const node = this.nodes.get(nodeId);
    if (!node) {
      return false;
    }

    if (node.type === 'SEGMENT_AND_FILTER' || node.type === 'MERGE') {
      return false;
    }

    return true;
  }

  canDeleteNode(nodeId) {
    const node = this.nodes.get(nodeId);
    if (!node) {
      return false;
    }

    if (node.type === 'SEGMENT_AND_FILTER' || node.type === 'MERGE') {
      return false;
    }

    return true;
  }

  canBranchNode(nodeId) {
    if (this.hasAbTest) {
      return false;
    }
    const node = this.nodes.get(nodeId);
    if (!node) {
      return false;
    }
    if (node.type === 'MERGE') {
      return false;
    }
    const nextNode = this.nodes.get(node.next);
    if (nextNode && nextNode.type === 'MERGE') {
      // Having another node before the merge node is required based on how we create the control group
      return false;
    }
    return true;
  }

  getNextNodeId(nodeId) {
    const node = this.nodes.get(nodeId);
    return node?.next;
  }

  getNodesCanAddAtEdge(edgeId, isPostMerge) {
    const [startNodeId, endNodeId] = this._parseEdgeId(edgeId);
    return this._getNodesCanAddAtEdge(startNodeId, endNodeId, isPostMerge);
  }

  _getNodesCanAddAtEdge(startNodeId, endNodeId, isPostMerge) {
    const startNode = this.nodes.get(startNodeId);
    const endNode = this.nodes.get(endNodeId);
    if (!(startNode && endNode)) {
      return [];
    }

    const isTreatment = startNode.isTreatment || endNode.isTreatment;

    const usedTypes = [startNode.type, endNode.type];
    let currentNode = startNode;
    while (currentNode?.previous >= 0) {
      currentNode = this.nodes.get(currentNode.previous);
      if (currentNode) {
        usedTypes.push(currentNode.type);
      }
    }
    currentNode = endNode;
    while (currentNode?.next) {
      currentNode = this.nodes.get(currentNode.next);
      if (currentNode) {
        usedTypes.push(currentNode.type);
      }
    }
    const abNode = this.hasAbTest ? this.nodes.get(this.abNodeId) : null;
    if (this.hasAbTest && !isTreatment) {
      // If we're not in a treatment but have an AB test, then we need to check all treatments
      // We can't add a node here if it exists in any treatment branch
      for (const [, allocationStartId] of abNode.treatments) {
        currentNode = this.nodes.get(allocationStartId);
        if (currentNode) {
          usedTypes.push(currentNode.type);
        }
        while (currentNode && currentNode.next !== abNode.testEndNode && currentNode.next >= 0) {
          currentNode = this.nodes.get(currentNode.next);
          if (currentNode) {
            usedTypes.push(currentNode.type);
          }
        }
      }
    }
    const availableTypes = [];
    for (const stepType of WORKFLOW_RESOURCE_STEP_TYPES) {
      if (!usedTypes.includes(stepType)) {
        availableTypes.push(stepType);
      }
    }

    // Also include adjoining node types, if it isn't on the edge of an AB test
    if (
      !availableTypes.includes(startNode.type) &&
      WORKFLOW_RESOURCE_STEP_TYPES.includes(startNode.type) &&
      (!this.hasAbTest || !isPostMerge)
    ) {
      availableTypes.push(startNode.type);
    }
    if (
      !availableTypes.includes(endNode.type) &&
      WORKFLOW_RESOURCE_STEP_TYPES.includes(endNode.type) &&
      (!this.hasAbTest || endNode.id !== abNode.testEndNode || isPostMerge)
    ) {
      availableTypes.push(endNode.type);
    }
    return availableTypes.sort();
  }

  getNodesForAbTestEndOptions(nodeId) {
    const options = [];
    let currentNode = this.nodes.get(nodeId);
    currentNode = this.nodes.get(currentNode.next);
    while (currentNode.next >= 0 && currentNode.type !== 'MERGE') {
      options.push(currentNode);
      currentNode = this.nodes.get(currentNode.next);
    }

    return options;
  }

  getNodeByType(nodeType, startNodeId) {
    if (startNodeId === undefined || startNodeId === null) {
      startNodeId = this.startAt;
    }

    let currentNode = this.nodes.get(startNodeId);
    while (currentNode && currentNode.type !== nodeType) {
      currentNode = this.nodes.get(currentNode.next);
    }
    return currentNode.id;
  }

  edgeCanAdd(from, to) {
    const options = this._getNodesCanAddAtEdge(from, to);
    return options.length > 0;
  }

  _parseEdgeId(edgeId) {
    const pieces = edgeId.split('-');
    if (!isNaN(pieces[1])) {
      pieces[1] = parseInt(pieces[1], 10);
    }
    if (!isNaN(pieces[2])) {
      pieces[2] = parseInt(pieces[2], 10);
    }
    return [pieces[1], pieces[2]];
  }

  getNodeParameters(nodeId) {
    return this.nodes.get(nodeId).parameters;
  }

  setNodeParameters(nodeId, newParameters) {
    this.nodes.get(nodeId).parameters = newParameters;
  }

  getNode(nodeId) {
    return this.nodes.get(nodeId);
  }

  getControlBranchStart() {
    if (!this.hasAbTest) {
      return '';
    }
    const abNode = this.nodes.get(this.abNodeId);
    return abNode.next;
  }

  getAbTreatmentBranches() {
    const branches = new Map();

    if (!this.hasAbTest) {
      return branches;
    }

    const abNode = this.nodes.get(this.abNodeId);

    for (const [treatmentName, treatmentStartId] of abNode.treatments) {
      branches.set(treatmentStartId, treatmentName);
    }
    return branches;
  }

  tryMarkBranchUntested(nodeId) {
    const abBranch = this.getNodeBranch(nodeId);
    if (abBranch) {
      this._markBranchTestingStatus(abBranch, false);
    }
  }

  getNodeBranch(nodeId) {
    let node = this.getNode(nodeId);
    if (node && !node.isTreatment) {
      return null;
    }

    let previousNode;
    while (node.id !== this.abNodeId) {
      previousNode = node;
      node = this.getNode(node.previous);
    }

    return previousNode.id;
  }

  _markBranchTestingStatus(branch, status) {
    const abNode = this.getNode(this.abNodeId);
    const allocations = abNode.parameters.allocations;
    for (const allocation of allocations) {
      if (allocation.next === branch || allocation.name === branch) {
        allocation.isTested = status;
        return true;
      }
    }
    return false;
  }

  _allBranchesTested() {
    const abNode = this.getNode(this.abNodeId);
    const allocations = abNode.parameters.allocations;
    for (const allocation of allocations) {
      if (!allocation.isTested) {
        return false;
      }
    }
    return true;
  }

  markTested(branch) {
    if (!this.hasAbTest) {
      this.isTested = true;
      return true;
    }

    if (!branch) {
      return false;
    }

    if (this._markBranchTestingStatus(branch, true)) {
      if (this._allBranchesTested()) {
        this.isTested = true;
      }
      return true;
    }
    return false;
  }

  getResourcesList() {
    const resources = [];
    for (const node of this.nodes.values()) {
      if (WORKFLOW_RESOURCE_STEP_TYPES.includes(node.type)) {
        resources.push(node.parameters.resourceId);
      }
    }
    return resources;
  }

  // callbacks: add new element, remove element, edit element; for a/b test: add new treatment group.
  asFlowElements(editable) {
    const flowElements = [];
    if (this.nodes.size === 0) {
      return flowElements;
    }

    flowElements.push(_.cloneDeep(START));
    flowElements.push(_.cloneDeep(END));
    // first run through control group
    let elementsFromGraphLine = this._flowElementsFromGraphLine(
      this.startAt,
      nodeId => nodeId >= 0,
      false,
      this.callbacks,
      editable
    );
    elementsFromGraphLine.forEach(element => flowElements.push(element));
    flowElements.push(this._edge('START', this.startAt, '', this.callbacks, null, null, null, editable));
    // if there is a/b, then run through treatments
    if (this.hasAbTest) {
      const abNode = this.nodes.get(this.abNodeId);
      if (abNode.testEndNode >= 0) {
        let allMergeEdgesCanAdd = true;

        const nodeIdsBeforeMerge = this._getNodesBeforeMerge();
        for (const nodeId of nodeIdsBeforeMerge) {
          if (!this.edgeCanAdd(nodeId, abNode.testEndNode)) {
            allMergeEdgesCanAdd = false;
          }
        }
        for (const nodeId of nodeIdsBeforeMerge) {
          flowElements.push(
            this._edge(nodeId, abNode.testEndNode, '', this.callbacks, false, true, allMergeEdgesCanAdd, editable)
          );
          allMergeEdgesCanAdd = false;
        }
      }
    }

    return this._layoutedFlowElements(flowElements);
  }

  _getNodesBeforeMerge() {
    const nodeIdsBeforeMerge = [];
    const abNode = this.nodes.get(this.abNodeId);
    for (const [nodeId, nodeObj] of this.nodes) {
      if (nodeObj.next === abNode.testEndNode) {
        nodeIdsBeforeMerge.push(nodeId);
      }
    }
    return nodeIdsBeforeMerge;
  }

  _flowElementsFromGraphLine(startAtId, endPredicament, isTreatment, callbacks, editable) {
    const flowElements = [];
    const abNode = this.nodes.get(this.abNodeId);
    let previousId = undefined;
    let nextId = startAtId;
    while (endPredicament(nextId)) {
      const node = this.nodes.get(nextId);
      if (!node) {
        break;
      }

      if (!isTreatment && node.isTreatment) {
        // if the node is a treatment, but we're not running a treatment line, then don't add it here
        // this prevents performance problems and unexpected functional behavior
        nextId = node.next;
        previousId = node.id;
        continue;
      }

      flowElements.push(this._flowElement(node, callbacks, editable));
      if (previousId !== undefined && (!this.hasAbTest || nextId !== abNode.testEndNode)) {
        // We don't want to push an edge if we're on the merge node, because we have the extra virtual merge node
        flowElements.push(
          this._edge(previousId, node.id, '', callbacks, previousId === this.abNodeId, null, null, editable)
        );
      }

      if (nextId === this.abNodeId) {
        for (const [treatmentName, treatmentStartId] of abNode.treatments) {
          if (treatmentStartId === abNode.next) {
            continue;
          }
          const elementsFromGraphLine = this._flowElementsFromGraphLine(
            treatmentStartId,
            nodeId => nodeId !== abNode.testEndNode && nodeId >= 0,
            true,
            callbacks,
            editable
          );
          elementsFromGraphLine.forEach(element => flowElements.push(element));
          flowElements.push(
            this._edge(abNode.id, treatmentStartId, treatmentName, callbacks, true, null, null, editable)
          );
        }
      }

      previousId = node.id;
      nextId = node.next;
    }

    if (!isTreatment || (this.hasAbTest && this.nodes.get(this.abNodeId)?.testEndNode < 0)) {
      flowElements.push(this._edge(previousId, 'END', '', callbacks, null, null, null, editable));
    }
    return flowElements;
  }

  _flowElement(node, callbacks, editable) {
    if (!node) {
      return;
    }

    // Determine node type
    let nodeType = 'WorkflowNode';
    if (node.id === this.abNodeId) {
      nodeType = 'BranchingNode';
    }

    return {
      id: node.id.toString(),
      type: nodeType,
      data: {
        node: node,
        label: <>{this.getDisplayName(node.type)}</>,
        canEdit: editable && this.canEditNode(node.id),
        canDelete: editable && this.canDeleteNode(node.id),
        canBranch: editable && this.canBranchNode(node.id),
        callbacks: callbacks,
      },
    };
  }

  getDisplayName(nodeType) {
    switch (nodeType) {
      case WORKFLOW_STEP_TYPES.AB:
        return WORKFLOW_NODE_LABELS.AB;
      case WORKFLOW_STEP_TYPES.Luma:
        return WORKFLOW_NODE_LABELS.Luma;
      case WORKFLOW_STEP_TYPES.MachineTranslation:
        return WORKFLOW_NODE_LABELS.MachineTranslation;
      case WORKFLOW_STEP_TYPES.Merge:
        return WORKFLOW_NODE_LABELS.Merge;
      case WORKFLOW_STEP_TYPES.SegmentAndFilter:
        return WORKFLOW_NODE_LABELS.SegmentAndFilter;
      case WORKFLOW_STEP_TYPES.TerminologyBase:
        return WORKFLOW_NODE_LABELS.TerminologyBase;
      case WORKFLOW_STEP_TYPES.TranslationMemory:
        return WORKFLOW_NODE_LABELS.TranslationMemory;
      default:
        return nodeType;
    }
  }

  _edge(from, to, label = '', callbacks, abSplitEdge, abMergeEdge, mergeEdgesCanAdd, editable) {
    const edgeId = this._createEdgeId(from, to);
    return {
      id: edgeId,
      source: from.toString(),
      target: to.toString(),
      animated: false,
      type: 'CustomEdge',
      arrowHeadType: 'arrowclosed',
      label: label,
      data: {
        addNewNodeCallback: callbacks.addNewNode,
        canAdd: editable && this.edgeCanAdd(from, to),
        abSplitEdge: abSplitEdge,
        abMergeEdge: abMergeEdge,
        mergeEdgesCanAdd: mergeEdgesCanAdd,
      },
    };
  }

  _createEdgeId(from, to) {
    return `e-${from}-${to}`;
  }

  // a copy-paste from https://reactflow.dev/examples/layouting/
  _layoutedFlowElements(elements) {
    this.width = 0;
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: 'LR' });

    elements.forEach(el => {
      if (isNode(el)) {
        const [nodeWidth, nodeHeight] = this._getNodeWidthHeight(el.id);
        dagreGraph.setNode(el.id, { width: nodeWidth, height: nodeHeight });
      } else {
        dagreGraph.setEdge(el.source, el.target);
      }
    });

    dagre.layout(dagreGraph);

    return elements.map(el => {
      if (isNode(el)) {
        const nodeWithPosition = dagreGraph.node(el.id);
        el.targetPosition = 'left';
        el.sourcePosition = 'right';

        // unfortunately we need this little hack to pass a slightly different position
        // to notify react flow about the change. Moreover we are shifting the dagre node position
        // (anchor=center center) to the top left so it matches the react flow node anchor point (top left).
        if (nodeWithPosition.x + nodeWithPosition.width / 2 > this.width) {
          this.width = nodeWithPosition.x + nodeWithPosition.width / 2;
        }
        el.position = {
          x: nodeWithPosition.x - nodeWithPosition.width / 2 + Math.random() / 1000,
          y: nodeWithPosition.y - nodeWithPosition.height / 2,
        };
      }

      return el;
    });
  }

  _getNodeWidthHeight(nodeId) {
    if (this.hasAbTest) {
      const numericNodeId = parseInt(nodeId, 10);
      if (numericNodeId === this.abNodeId) {
        return [270, 91];
      }
      const abNode = this.getNode(this.abNodeId);
      const node = this.getNode(numericNodeId);
      if (node?.next === abNode.testEndNode) {
        return [220, 91];
      }
    }
    switch (nodeId) {
      case 'START':
      case 'END':
        return [100, 34];
      default:
        return [150, 91];
    }
  }

  getSteps() {
    const steps = [];
    this.nodes.forEach(value => steps.push(value));
    return steps;
  }

  getNumberOfBranches() {
    if (!this.hasAbTest) {
      return 1;
    }
    const abNode = this.getNode(this.abNodeId);
    return abNode.treatments.size;
  }
}
